/**
 * useMyEnrollments Hook
 *
 * Fetches the user's own enrollments and slices them into active / pending / past
 * for the MyEnrollmentsPage UI.
 */

import { useState, useEffect, useCallback } from 'react';
import { getMyEnrollments } from '../../services/coursesService';
import type { EnrollmentWithCourse } from '../../types/courses.types';
import { parseLocalDate } from '../../utils/date';

interface UseMyEnrollmentsResult {
  isLoading: boolean;
  error: string | null;
  enrollments: EnrollmentWithCourse[];
  activeEnrollments: EnrollmentWithCourse[];
  pendingEnrollments: EnrollmentWithCourse[];
  pastEnrollments: EnrollmentWithCourse[];
  refresh: () => void;
}

function isPastCourse(enrollment: EnrollmentWithCourse): boolean {
  const courseDate = parseLocalDate(enrollment.courseDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return courseDate < today;
}

export function useMyEnrollments(
  employeeId: number | null,
  isAuthenticated: boolean
): UseMyEnrollmentsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrollments, setEnrollments] = useState<EnrollmentWithCourse[]>([]);

  const fetchEnrollments = useCallback(async () => {
    if (!employeeId || !isAuthenticated) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getMyEnrollments();
      setEnrollments(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    } finally {
      setIsLoading(false);
    }
  }, [employeeId, isAuthenticated]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  const activeEnrollments = enrollments.filter(
    (e) => e.status === 'approved' && !isPastCourse(e)
  );
  const pendingEnrollments = enrollments.filter(
    (e) => e.status === 'pending' && !isPastCourse(e)
  );
  const pastEnrollments = enrollments.filter(
    (e) =>
      isPastCourse(e) ||
      e.status === 'cancelled' ||
      e.status === 'rejected' ||
      e.status === 'awaiting_credit'
  );

  return {
    isLoading,
    error,
    enrollments,
    activeEnrollments,
    pendingEnrollments,
    pastEnrollments,
    refresh: fetchEnrollments,
  };
}
